import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Phone, Mail, Globe, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = [
  { value: "new", label: "Yeni" },
  { value: "contacted", label: "İletişimde" },
  { value: "trial_scheduled", label: "Deneme Randevusu" },
  { value: "converted", label: "Üye Oldu" },
  { value: "lost", label: "Kaybedildi" },
];
const STATUS_CLS = {
  new: "text-[#FF5500]", contacted: "text-[#3B82F6]", trial_scheduled: "text-[#F59E0B]",
  converted: "text-[#10B981]", lost: "text-[#68687B]",
};

export default function CRM() {
  const [leads, setLeads] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_interest: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.put(`/leads/${id}`, { status }); toast.success("Durum güncellendi"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.post("/leads", form); toast.success("Aday eklendi"); setOpen(false); setForm({ name: "", phone: "", email: "", service_interest: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const counts = (leads || []).reduce((a, l) => { a[l.status] = (a[l.status] || 0) + 1; return a; }, {});

  return (
    <div data-testid="admin-crm-page">
      <PageHeader title="CRM & Adaylar" subtitle="Web sitesi deneme formundan ve manuel gelen adaylar"
        action={<Button onClick={() => setOpen(true)} data-testid="add-new-lead-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl"><Plus size={18} className="mr-1" /> Yeni Aday</Button>} />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        {STATUSES.map((s) => (
          <div key={s.value} className="rounded-xl bg-[#13131C] border border-[#222230] p-3">
            <p className="text-xs text-[#68687B]">{s.label}</p>
            <p className={`font-mono font-extrabold text-2xl mt-0.5 ${STATUS_CLS[s.value]}`}>{counts[s.value] || 0}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl bg-[#FF5500]/5 border border-[#FF5500]/20 p-3 mb-5 flex items-start gap-2">
        <Globe size={16} className="text-[#FF5500] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-[#A0A0B2]">getup.fit deneme antrenmanı formu <code className="text-[#FF5500]">POST /api/leads/public</code> ucuna bağlanınca gelen başvurular otomatik burada "Web Sitesi" kaynağıyla listelenir.</p>
      </div>

      {leads === null ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl bg-[#13131C]" />)}</div>
      ) : leads.length === 0 ? (
        <div className="text-center py-20 text-[#68687B]">Henüz aday yok.</div>
      ) : (
        <div className="rounded-2xl border border-[#222230] overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-[#181824] text-[#68687B] text-xs uppercase">
              <tr><th className="px-4 py-3 text-left">Aday</th><th className="px-4 py-3 text-left hidden sm:table-cell">İlgi</th><th className="px-4 py-3 text-left hidden md:table-cell">Kaynak</th><th className="px-4 py-3 text-left">Durum</th></tr>
            </thead>
            <tbody>
              {leads.map((l, i) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className={`border-t border-[#1A1A24] ${i % 2 ? "bg-[#161622]" : "bg-[#13131C]"}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-white">{l.name}</p>
                    <div className="flex items-center gap-3 text-xs text-[#68687B] mt-0.5">
                      {l.phone && <span className="flex items-center gap-1"><Phone size={11} /> {l.phone}</span>}
                      {l.email && <span className="hidden sm:flex items-center gap-1"><Mail size={11} /> {l.email}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#A0A0B2] hidden sm:table-cell">{l.service_interest || "—"}{l.studio ? ` · ${l.studio}` : ""}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${l.source === "website" ? "bg-[#FF5500]/10 text-[#FF5500] border-[#FF5500]/30" : "bg-[#68687B]/15 text-[#A0A0B2] border-[#68687B]/30"}`}>
                      {l.source === "website" ? "Web Sitesi" : "Manuel"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                      <SelectTrigger data-testid={`lead-status-${l.id}`} className="h-8 w-40 bg-[#0E0E14] border-[#262636] text-white text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white">{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><UserPlus size={18} className="text-[#FF5500]" /> Yeni Aday</DialogTitle><DialogDescription className="text-[#68687B]">Telefonla/manuel gelen adayı ekle.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-1">
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Ad Soyad</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-form-name" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Telefon</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">E-posta</label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            </div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">İlgilendiği Hizmet</label><Input value={form.service_interest} onChange={(e) => setForm({ ...form, service_interest: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="lead-form-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
